import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';
import { JobLead, JobLeadStatus, ApproveJobLeadPayload } from '../../types';

const STATUS_STYLE: Record<JobLeadStatus, { bg: string; color: string; label: string }> = {
  [JobLeadStatus.PENDING_APPROVAL]: { bg: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)', label: '🟡 Review' },
  [JobLeadStatus.APPROVED]:         { bg: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', label: '🟢 Approved' },
  [JobLeadStatus.REJECTED]:         { bg: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', label: '🔴 Rejected' },
  [JobLeadStatus.FULFILLED]:        { bg: 'var(--sc-brand-primary-bg)', color: 'var(--sc-brand-primary)', label: '🟢 Fulfilled' },
};

const FILTER_TABS: { key: JobLeadStatus | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: JobLeadStatus.PENDING_APPROVAL, label: 'Pending' },
  { key: JobLeadStatus.APPROVED,         label: 'Approved' },
  { key: JobLeadStatus.REJECTED,         label: 'Rejected' },
  { key: JobLeadStatus.FULFILLED,        label: 'Fulfilled' },
];

interface ApproveModalProps {
  lead: JobLead;
  onClose: () => void;
  onConfirm: (id: string, payload: ApproveJobLeadPayload) => Promise<void>;
}
const ApproveModal: React.FC<ApproveModalProps> = ({ lead, onClose, onConfirm }) => {
  const [adminPct, setAdminPct] = useState(100 - (lead.requested_referrer_share_pct ?? 30));
  const [referrerPct, setReferrerPct] = useState(lead.requested_referrer_share_pct ?? 30);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const syncAdmin = (v: number) => {
    setAdminPct(v);
    setReferrerPct(100 - v);
  };
  const syncReferrer = (v: number) => {
    setReferrerPct(v);
    setAdminPct(100 - v);
  };

  const platformCommission = (lead.estimated_value ?? 0) * (lead.platform_commission_rate ?? 0.10);
  const referrerPayout = (platformCommission * referrerPct) / 100;
  const adminPayout = (platformCommission * adminPct) / 100;

  const handleConfirm = async () => {
    if (adminPct + referrerPct !== 100) { setErr('Shares must add up to 100%.'); return; }
    setSaving(true);
    setErr('');
    try {
      await onConfirm(lead.id, { admin_share_pct: adminPct, referrer_share_pct: referrerPct, notes: notes || undefined });
      onClose();
    } catch {
      setErr('Failed to approve. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={m.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={m.modal}>
        <h3 style={m.title}>Approve Job Lead</h3>
        <p style={m.subtitle}>
          Set the profit split for <strong>{lead.referrer.first_name} {lead.referrer.last_name}</strong>'s referral.
          The split applies to the platform's 12% commission on the final deal value.
        </p>

        {lead.estimated_value && (
          <div style={m.dealPreview}>
            <div style={m.dealRow}><span>Estimated deal value</span><strong>${lead.estimated_value.toLocaleString()}</strong></div>
            <div style={m.dealRow}><span>Platform commission ({((lead.platform_commission_rate ?? 0.10) * 100).toFixed(0)}%)</span><strong>${platformCommission.toFixed(2)}</strong></div>
            <div style={m.dealRow}><span>Surveyor requested share</span><strong>{lead.requested_referrer_share_pct}%</strong></div>
            <div style={{ height: 1, background: 'var(--sc-border)', margin: '8px 0' }} />
            <div style={m.dealRow}>
              <span style={{ color: 'var(--sc-success)' }}>Admin keeps ({adminPct}%)</span>
              <strong style={{ color: 'var(--sc-success)' }}>${adminPayout.toFixed(2)}</strong>
            </div>
            <div style={m.dealRow}>
              <span style={{ color: 'var(--sc-brand-primary)' }}>Referrer earns ({referrerPct}%)</span>
              <strong style={{ color: 'var(--sc-brand-primary)' }}>${referrerPayout.toFixed(2)}</strong>
            </div>
          </div>
        )}

        <label style={m.label}>Admin Share %</label>
        <div style={m.sliderRow}>
          <input
            type="range" min={0} max={100} step={5}
            value={adminPct}
            onChange={(e) => syncAdmin(Number(e.target.value))}
            style={{ flex: 1, accentColor: 'var(--sc-brand-primary)' }}
          />
          <input
            type="number" min={0} max={100}
            value={adminPct}
            onChange={(e) => syncAdmin(Math.min(100, Math.max(0, Number(e.target.value))))}
            style={m.pctInput}
          />
          <span style={m.pctLabel}>%</span>
        </div>

        <label style={m.label}>Referrer Share %</label>
        <div style={m.sliderRow}>
          <input
            type="range" min={0} max={100} step={5}
            value={referrerPct}
            onChange={(e) => syncReferrer(Number(e.target.value))}
            style={{ flex: 1, accentColor: 'var(--sc-brand-primary)' }}
          />
          <input
            type="number" min={0} max={100}
            value={referrerPct}
            onChange={(e) => syncReferrer(Math.min(100, Math.max(0, Number(e.target.value))))}
            style={m.pctInput}
          />
          <span style={m.pctLabel}>%</span>
        </div>

        {adminPct + referrerPct !== 100 && (
          <div style={m.sumWarning}>Shares total {adminPct + referrerPct}% — must equal 100%</div>
        )}

        <label style={m.label}>Admin Notes (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={m.textarea}
          placeholder="Any notes for the surveyor or for the platform record…"
        />

        {err && <div style={m.err}>{err}</div>}

        <div style={m.btnRow}>
          <button style={m.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={m.confirmBtn} onClick={handleConfirm} disabled={saving || adminPct + referrerPct !== 100}>
            {saving ? 'Approving…' : '✓ Approve & Set Split'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface RejectModalProps {
  lead: JobLead;
  onClose: () => void;
  onConfirm: (id: string, reason: string) => Promise<void>;
}
const RejectModal: React.FC<RejectModalProps> = ({ lead, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleConfirm = async () => {
    if (!reason.trim()) { setErr('Please provide a rejection reason.'); return; }
    setSaving(true);
    setErr('');
    try {
      await onConfirm(lead.id, reason.trim());
      onClose();
    } catch {
      setErr('Failed to reject. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={m.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...m.modal, maxWidth: 440 }}>
        <h3 style={m.title}>Reject Job Lead</h3>
        <p style={m.subtitle}>
          Rejecting lead from <strong>{lead.referrer.first_name} {lead.referrer.last_name}</strong>.
          The surveyor will be notified with your reason.
        </p>
        <label style={m.label}>Rejection Reason <span style={{ color: 'var(--sc-error)' }}>*</span></label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={m.textarea}
          placeholder="e.g. Duplicate lead already on platform, insufficient information, etc."
        />
        {err && <div style={m.err}>{err}</div>}
        <div style={m.btnRow}>
          <button style={m.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...m.confirmBtn, background: 'var(--sc-error)' }} onClick={handleConfirm} disabled={saving}>
            {saving ? 'Rejecting…' : '✕ Reject Lead'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const AdminJobLeads: React.FC = () => {
  const [leads, setLeads] = useState<JobLead[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<JobLeadStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [approveTarget, setApproveTarget] = useState<JobLead | null>(null);
  const [rejectTarget, setRejectTarget] = useState<JobLead | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => { loadLeads(); }, [filter, search]);

  const loadLeads = async () => {
    setIsLoading(true);
    try {
      const res = await adminService.listJobLeads({
        status: filter !== 'ALL' ? filter : undefined,
        search: search || undefined,
      });
      setLeads(res.results ?? []);
      setTotal(res.count ?? 0);
    } catch {
      setLeads([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: string, payload: ApproveJobLeadPayload) => {
    await adminService.approveJobLead(id, payload);
    setLeads((prev) =>
      prev.map((l) => l.id === id ? { ...l, ...payload, status: JobLeadStatus.APPROVED } : l),
    );
  };

  const handleReject = async (id: string, reason: string) => {
    await adminService.rejectJobLead(id, reason);
    setLeads((prev) =>
      prev.map((l) => l.id === id ? { ...l, rejection_reason: reason, status: JobLeadStatus.REJECTED } : l),
    );
  };

  const handleUnreject = async (id: string) => {
    await adminService.unrejectJobLead(id);
    setLeads((prev) =>
      prev.map((l) => l.id === id ? { ...l, rejection_reason: '', status: JobLeadStatus.PENDING_APPROVAL } : l),
    );
  };

  const pendingCount = leads.filter((l) => l.status === JobLeadStatus.PENDING_APPROVAL).length;

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-ZW', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div style={s.page}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <h1 style={s.pageTitle}>Job Leads</h1>
          <p style={s.pageSubtitle}>
            Surveyor-submitted job opportunities awaiting admin review. Approve with a profit split or reject with a reason.
          </p>
        </div>
        <div style={s.statsRow}>
          {[
            { label: 'Total', value: total, color: 'var(--sc-brand-primary)' },
            { label: 'Pending', value: leads.filter((l) => l.status === JobLeadStatus.PENDING_APPROVAL).length, color: 'var(--sc-warning)' },
            { label: 'Approved', value: leads.filter((l) => l.status === JobLeadStatus.APPROVED).length, color: 'var(--sc-success)' },
            { label: 'Fulfilled', value: leads.filter((l) => l.status === JobLeadStatus.FULFILLED).length, color: 'var(--sc-brand-primary-light)' },
          ].map((stat) => (
            <div key={stat.label} style={s.statPill}>
              <span style={{ ...s.statNum, color: stat.color }}>{stat.value}</span>
              <span style={s.statLabel}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pending alert banner ── */}
      {pendingCount > 0 && (
        <div style={s.alertBanner}>
          ⚠️ <strong>{pendingCount}</strong> {pendingCount === 1 ? 'lead is' : 'leads are'} awaiting your review.
        </div>
      )}

      {/* ── Toolbar ── */}
      <div style={s.toolbar}>
        <div style={s.tabs}>
          {FILTER_TABS.map((t) => (
            <button
              key={t.key}
              style={{ ...s.tab, ...(filter === t.key ? s.activeTab : {}) }}
              onClick={() => setFilter(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <input
          style={s.search}
          placeholder="Search by surveyor, service, location…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ── Table ── */}
      {isLoading ? (
        <div style={s.centred}><div style={s.spinner} /></div>
      ) : leads.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
          <p>No job leads found.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={s.table}>
            {/* thead */}
            <div style={s.thead}>
              <div style={{ ...s.th, flex: 1.8 }}>Type</div>
              <div style={{ ...s.th, flex: 1.5 }}>Referrer</div>
              <div style={{ ...s.th, flex: 1.5 }}>Client</div>
              <div style={{ ...s.th, flex: 2 }}>Location</div>
              <div style={{ ...s.th, flex: 0.9 }}>Value</div>
              <div style={{ ...s.th, flex: 1 }}>Split</div>
              <div style={{ ...s.th, flex: 1 }}>Stat</div>
              <div style={{ ...s.th, flex: 0.8 }}>Date</div>
              <div style={{ ...s.th, flex: 1 }}>Action</div>
            </div>

            {leads.map((lead) => {
              const st = STATUS_STYLE[lead.status];
              const isExpanded = expandedId === lead.id;
              return (
                <React.Fragment key={lead.id}>
                  <div
                    style={{ ...s.row, ...(lead.status === JobLeadStatus.PENDING_APPROVAL ? s.pendingRow : {}) }}
                    onClick={() => setExpandedId(isExpanded ? null : lead.id)}
                  >
                    <div style={{ ...s.td, flex: 1.8, fontWeight: 600, fontSize: 13, color: 'var(--sc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.service_type}</div>
                    <div style={{ ...s.td, flex: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lead.referrer.first_name} {lead.referrer.last_name}
                    </div>
                    <div style={{ ...s.td, flex: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.client_name}</div>
                    <div style={{ ...s.td, flex: 2, color: 'var(--sc-text-secondary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.location_address}</div>
                    <div style={{ ...s.td, flex: 0.9, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lead.estimated_value ? `$${lead.estimated_value.toLocaleString()}` : '—'}
                    </div>
                    <div style={{ ...s.td, flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lead.status === JobLeadStatus.PENDING_APPROVAL ? (
                        <span style={{ color: 'var(--sc-brand-primary)', fontWeight: 600 }}>Req. {lead.requested_referrer_share_pct}%</span>
                      ) : (
                        <span>
                          <span style={{ color: 'var(--sc-success)', fontWeight: 600 }}>{lead.admin_share_pct}%</span>
                          {' / '}
                          <span style={{ color: 'var(--sc-brand-primary)', fontWeight: 600 }}>{lead.referrer_share_pct}%</span>
                        </span>
                      )}
                    </div>
                    <div style={{ ...s.td, flex: 1 }}>
                      <span style={{ ...s.badge, background: st.bg, color: st.color }}>{st.label}</span>
                    </div>
                    <div style={{ ...s.td, flex: 0.8, fontSize: 12, color: 'var(--sc-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDate(lead.created_at)}</div>
                    <div style={{ ...s.td, flex: 1 }} onClick={(e) => e.stopPropagation()}>
                      {lead.status === JobLeadStatus.PENDING_APPROVAL && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button style={s.approveBtn} onClick={() => setApproveTarget(lead)}>Approve</button>
                          <button style={s.rejectBtn} onClick={() => setRejectTarget(lead)}>Reject</button>
                        </div>
                      )}
                      {lead.status === JobLeadStatus.APPROVED && (
                        <button style={s.rejectBtn} onClick={() => setRejectTarget(lead)}>Revoke</button>
                      )}
                      {lead.status === JobLeadStatus.REJECTED && (
                        <button style={s.approveBtn} onClick={() => handleUnreject(lead.id)}>Restore</button>
                      )}
                    </div>
                  </div>

                  {/* Expanded row */}
                  {isExpanded && (
                    <div style={s.expandRow}>
                      <div style={s.expandGrid}>
                        <div>
                          <span style={s.expandLabel}>Client Phone</span>
                          <span style={s.expandVal}>{lead.client_phone}</span>
                        </div>
                        <div>
                          <span style={s.expandLabel}>Referrer Phone</span>
                          <span style={s.expandVal}>{lead.referrer.phone}</span>
                        </div>
                        {lead.payout_amount !== undefined && (
                          <div>
                            <span style={s.expandLabel}>Referrer Payout</span>
                            <span style={{ ...s.expandVal, color: 'var(--sc-brand-primary)', fontWeight: 700 }}>${lead.payout_amount.toFixed(2)}</span>
                          </div>
                        )}
                        {lead.linked_deal_id && (
                          <div>
                            <span style={s.expandLabel}>Linked Deal</span>
                            <span style={s.expandVal}>{lead.linked_deal_id}</span>
                          </div>
                        )}
                        <div>
                          <span style={s.expandLabel}>Requested Share</span>
                          <span style={s.expandVal}>{lead.requested_referrer_share_pct}%</span>
                        </div>
                        <div>
                          <span style={s.expandLabel}>Platform Commission</span>
                          <span style={s.expandVal}>{((lead.platform_commission_rate ?? 0.10) * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                      <p style={s.expandDesc}>{lead.description}</p>
                      {lead.rejection_reason && (
                        <div style={s.rejectBox}>Rejection Reason: {lead.rejection_reason}</div>
                      )}
                      {lead.notes && (
                        <div style={s.noteBox}>Admin Notes: {lead.notes}</div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Page styles ──────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  page: { padding: '16px 20px', fontFamily: "'Inter', sans-serif" },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  pageTitle: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" },
  pageSubtitle: { fontSize: 13, color: 'var(--sc-text-secondary)', maxWidth: 560, lineHeight: 1.6, margin: 0 },
  statsRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  statPill: { background: 'var(--sc-bg-secondary)', borderRadius: 12, padding: '10px 16px', boxShadow: 'var(--sc-shadow-sm)', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72, border: '1px solid var(--sc-border)' },
  statNum: { fontSize: 18, fontWeight: 800 },
  statLabel: { fontSize: 10, color: 'var(--sc-text-tertiary)', fontWeight: 500, marginTop: 2 },

  alertBanner: { background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: 'var(--sc-warning-text)', marginBottom: 16 },

  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tab: { padding: '6px 14px', borderRadius: 20, border: '1px solid var(--sc-border)', background: 'var(--sc-bg-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--sc-text-secondary)', fontFamily: "'Inter', sans-serif", transition: 'all 0.2s' },
  activeTab: { background: 'var(--sc-brand-primary)', borderColor: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)' },
  search: { padding: '8px 14px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 13, fontFamily: "'Inter', sans-serif", color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)', outline: 'none', width: 260 },

  table: { background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', overflow: 'hidden', minWidth: 900, boxShadow: 'var(--sc-shadow-md)' },
  thead: { display: 'flex', background: 'var(--sc-bg-primary)', padding: '10px 16px', borderBottom: '1px solid var(--sc-border)' },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  row: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--sc-border)', cursor: 'pointer', transition: 'background 0.15s' },
  pendingRow: { background: 'var(--sc-warning-bg)', borderLeft: '3px solid var(--sc-warning)' },
  td: { flex: 1, fontSize: 13, color: 'var(--sc-text-primary)' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 },

  expandRow: { background: 'var(--sc-bg-primary)', padding: '14px 24px', borderBottom: '1px solid var(--sc-border)' },
  expandGrid: { display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 10 },
  expandLabel: { display: 'block', fontSize: 11, color: 'var(--sc-text-tertiary)', fontWeight: 500 },
  expandVal: { display: 'block', fontSize: 13, color: 'var(--sc-text-primary)', marginTop: 2 },
  expandDesc: { fontSize: 13, color: 'var(--sc-text-secondary)', lineHeight: 1.6, margin: '8px 0 0' },
  rejectBox: { background: 'var(--sc-error-bg)', borderRadius: 8, padding: '7px 12px', fontSize: 12, color: 'var(--sc-error-text)', marginTop: 8 },
  noteBox: { background: 'var(--sc-warning-bg)', borderRadius: 8, padding: '7px 12px', fontSize: 12, color: 'var(--sc-warning-text)', marginTop: 8 },

  approveBtn: { padding: '5px 12px', background: 'var(--sc-success)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  rejectBtn: { padding: '5px 12px', background: 'var(--sc-bg-secondary)', color: 'var(--sc-error)', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" },

  centred: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 },
  empty: { textAlign: 'center' as const, padding: '60px 20px', color: 'var(--sc-text-tertiary)', fontSize: 14 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
};

/* ── Modal styles ─────────────────────────────────────────── */
const m: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'var(--sc-bg-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--sc-bg-secondary)', borderRadius: 18, padding: '32px', width: '90vw', maxWidth: 520, boxShadow: 'var(--sc-shadow-xl)', color: 'var(--sc-text-primary)' },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 6px', fontFamily: "'Outfit', sans-serif" },
  subtitle: { fontSize: 13, color: 'var(--sc-text-secondary)', lineHeight: 1.6, margin: '0 0 18px' },
  dealPreview: { background: 'var(--sc-bg-primary)', borderRadius: 10, padding: '14px', marginBottom: 18, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--sc-border)' },
  dealRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--sc-text-secondary)' },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--sc-text-primary)', marginBottom: 6, marginTop: 14, display: 'block' },
  sliderRow: { display: 'flex', alignItems: 'center', gap: 10 },
  pctInput: { width: 58, padding: '6px 8px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, textAlign: 'center', fontFamily: "'Inter', sans-serif", background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)' },
  pctLabel: { fontSize: 13, color: 'var(--sc-text-secondary)', fontWeight: 600 },
  sumWarning: { fontSize: 12, color: 'var(--sc-error-text)', marginTop: 8, background: 'var(--sc-error-bg)', borderRadius: 6, padding: '5px 10px' },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 13, fontFamily: "'Inter', sans-serif", color: 'var(--sc-text-primary)', minHeight: 80, resize: 'vertical', outline: 'none', background: 'var(--sc-bg-input)' },
  err: { background: 'var(--sc-error-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--sc-error-text)', marginTop: 10 },
  btnRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 },
  cancelBtn: { padding: '10px 20px', background: 'var(--sc-bg-secondary)', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: 'var(--sc-text-secondary)', fontFamily: "'Inter', sans-serif" },
  confirmBtn: { padding: '10px 22px', background: 'var(--sc-success)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
};

